/**
 * PDP multi-pack picker: 1 / 4 / 12 packs.
 * Pack prices match the Buy 4, Save $5 automatic discount without mutating the live cart.
 * Never cart/add or cart/change here — preview adds previously unlocked the $125 free gift
 * and left stray single bags beside 4-packs.
 */
(function () {
  const DISCOUNT_PER_FOUR_CENTS = 500;

  function formatMoney(cents) {
    const picker = document.querySelector('multi-pack-picker');
    const format = picker?.dataset.moneyFormat || window.theme?.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    if (format.includes('{{amount_with_comma_separator}}')) {
      return format.replace(
        '{{amount_with_comma_separator}}',
        amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      );
    }
    return format.replace('{{amount}}', amount);
  }

  function estimatePackPricing(unitCents, quantity) {
    const qty = Math.max(1, quantity);
    const compareLine = unitCents * qty;
    const discountCents = Math.floor(qty / 4) * DISCOUNT_PER_FOUR_CENTS;
    const finalLine = compareLine - discountCents;
    const finalUnit = qty > 0 ? Math.round(finalLine / qty) : unitCents;

    return {
      originalUnit: unitCents,
      finalUnit,
      originalLine: compareLine,
      finalLine,
      savingsUnit: Math.max(0, unitCents - finalUnit),
      savingsLine: Math.max(0, discountCents),
    };
  }

  class MultiPackPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.formId = this.dataset.formId;
      this.variantId = this.dataset.variantId;
      this.unitCents = parseInt(this.dataset.unitCents, 10) || 0;
      this.cartButtonLabel = 'ADD {{ count }} {{ unit }} TO CART - {{ price }}';

      this.cards = [...this.querySelectorAll('.multi-pack-picker__card')];
      this.shipLine = this.querySelector('[data-multi-pack-ship-line]');
      this.inventoryLocation = (this.dataset.inventoryLocation || '').trim();
      if (!this.inventoryLocation && this.shipLine?.textContent) {
        const match = this.shipLine.textContent.match(/ships fresh from (.+)$/i);
        if (match) this.inventoryLocation = match[1].trim();
      }
      this.savingsLine = this.querySelector('[data-multi-pack-savings]');
      this.savingsAmount = this.querySelector('[data-multi-pack-savings-amount]');
      this.summaryTotal = this.querySelector('[data-multi-pack-summary-total]');
      this.footer = this.querySelector('[data-multi-pack-footer]');
      this.packProperty = this.querySelector('[data-multi-pack-property]');

      this.form = document.getElementById(this.formId);
      this.productForm = this.form?.closest('product-form');
      this.quantityInput = null;
      this.quantityBound = false;
      this.submitButton = this.form?.querySelector('[type="submit"][name="add"]');
      this.submitLabel = this.submitButton?.querySelector('span');

      this.selectedPack = this.dataset.defaultPack || '4';

      requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      this.moveBuyButtons();
      this.bindCards();
      this.bindQuantity();
      this.bindFormSubmit();

      this.applyEstimatedPackPrices();

      const initial = this.cards.find((c) => c.dataset.pack === this.selectedPack) || this.cards[0];
      if (initial) this.selectPack(initial, false);
    }

    getPackSizes() {
      return [...new Set(this.cards.map((c) => parseInt(c.dataset.pack, 10) || 1))];
    }

    applyEstimatedPackPrices() {
      this.cards.forEach((card) => {
        const packQty = parseInt(card.dataset.pack, 10) || 1;
        this.applyPricingToCard(card, estimatePackPricing(this.unitCents, packQty));
      });
      this.updateUi();
    }

    applyPricingToCard(card, pricing) {
      card.dataset.price = String(pricing.finalLine);
      card.dataset.compare = String(pricing.originalLine);
      card.dataset.savings = String(pricing.savingsLine);
      card.dataset.unitPrice = String(pricing.finalUnit);

      const hasDiscount = pricing.savingsLine > 0;

      const compareEl = card.querySelector('[data-multi-pack-compare]');
      const totalEl = card.querySelector('[data-multi-pack-total]');
      const saveEl = card.querySelector('[data-multi-pack-save]');

      if (totalEl) {
        totalEl.textContent = formatMoney(pricing.finalLine);
      }

      if (compareEl) {
        if (hasDiscount) {
          compareEl.textContent = formatMoney(pricing.originalLine);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      if (saveEl) {
        if (hasDiscount) {
          saveEl.textContent = `save ${formatMoney(pricing.savingsLine)}`;
          saveEl.hidden = false;
        } else {
          saveEl.hidden = true;
        }
      }

      card.classList.toggle('multi-pack-picker__card--highlight', hasDiscount);
    }

    resolveQuantityInput() {
      return (
        this.footer?.querySelector('input[name="quantity"]') ||
        document.querySelector(`#Quantity-${this.sectionId}[form="${this.formId}"]`) ||
        document.querySelector(`input[name="quantity"][form="${this.formId}"]`) ||
        this.form?.querySelector('input[name="quantity"]') ||
        null
      );
    }

    syncQuantityButtonState() {
      const wrapper = this.quantityInput?.closest('quantity-input');
      if (wrapper && typeof wrapper.validateQtyRules === 'function') {
        wrapper.validateQtyRules();
      }
    }

    syncPackQuantityRules(packNum) {
      if (!this.quantityInput) return;

      const pack = Math.max(1, packNum);
      const min = pack > 1 ? pack : 1;

      this.quantityInput.min = String(min);
      this.quantityInput.dataset.min = String(min);
      this.quantityInput.step = String(min);
    }

    setQuantityValue(value) {
      if (!this.quantityInput) return;

      const min = parseInt(this.quantityInput.min, 10) || 1;
      const step = parseInt(this.quantityInput.step, 10) || 1;
      let next = Math.max(min, parseInt(value, 10) || min);

      if (step > 1 && next % step !== 0) {
        next = Math.ceil(next / step) * step;
      }

      const nextValue = String(next);
      if (this.quantityInput.value !== nextValue) {
        this.quantityInput.value = nextValue;
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.syncAllQuantityInputs(nextValue);
      this.syncQuantityButtonState();
    }

    syncAllQuantityInputs(value) {
      const nextValue = String(value);
      const inputs = new Set();
      if (this.quantityInput) inputs.add(this.quantityInput);
      if (this.form) {
        this.form.querySelectorAll('input[name="quantity"]').forEach((input) => inputs.add(input));
      }
      if (this.formId) {
        document
          .querySelectorAll(`input[name="quantity"][form="${this.formId}"]`)
          .forEach((input) => inputs.add(input));
      }
      inputs.forEach((input) => {
        if (input.value !== nextValue) input.value = nextValue;
      });
    }

    moveBuyButtons() {
      if (!this.footer || !this.sectionId) return;
      const section = document.querySelector(`#ProductInfo-${this.sectionId}`);
      const buyRow = section?.querySelector('.both-bts-together--multi-pack');
      if (buyRow && !this.footer.contains(buyRow)) {
        this.footer.appendChild(buyRow);
      }
      this.syncMovedFormControls();
    }

    getVariantInputs() {
      if (!this.formId) return [];
      return [
        ...document.querySelectorAll(`input[name="id"][form="${this.formId}"]`),
        ...(this.form ? [...this.form.querySelectorAll('input[name="id"]')] : []),
      ];
    }

    ensureVariantIdOnForm() {
      const variantId = String(this.variantId || '').trim();
      if (!variantId || !this.formId) return;

      this.getVariantInputs().forEach((input) => {
        input.setAttribute('form', this.formId);
        input.disabled = false;
        input.value = variantId;
      });
    }

    syncMovedFormControls() {
      if (!this.formId) return;

      const submitBtn =
        this.footer?.querySelector(`#ProductSubmitButton-${this.sectionId}`) ||
        this.footer?.querySelector('[type="submit"][name="add"]');

      if (submitBtn) {
        submitBtn.setAttribute('form', this.formId);
        this.submitButton = submitBtn;
        this.submitLabel = submitBtn.querySelector('span');
      }

      this.quantityInput = this.resolveQuantityInput();
      this.ensureVariantIdOnForm();

      const productForm = this.productForm || this.form?.closest('product-form');
      if (productForm && submitBtn) {
        productForm.submitButton = submitBtn;
      }
    }

    bindCards() {
      this.cards.forEach((card) => {
        card.addEventListener('click', () => this.selectPack(card, false));
        card.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.selectPack(card, true);
          }
        });
      });
    }

    bindQuantity() {
      const input = this.resolveQuantityInput();
      if (!input) return;

      if (this.quantityInput !== input) {
        this.quantityInput = input;
      }

      if (this.quantityBound) return;

      input.addEventListener('change', () => this.updateUi());
      input.addEventListener('input', () => this.updateUi());
      this.quantityBound = true;
    }

    bindFormSubmit() {
      if (!this.form) return;

      this.form.addEventListener(
        'submit',
        () => {
          this.syncFormForSubmit();
        },
        true
      );
    }

    syncFormForSubmit() {
      const packNum = parseInt(this.selectedPack, 10) || 1;
      const lineQty = this.getLineQuantity();

      this.ensureVariantIdOnForm();
      this.syncAllQuantityInputs(String(lineQty));

      if (this.packProperty) {
        this.packProperty.value = String(packNum);
      }
    }

    getSelectedCard() {
      return this.cards.find((c) => c.dataset.pack === this.selectedPack);
    }

    selectPack(card, focusCard) {
      if (!card || card.disabled) return;
      this.selectedPack = card.dataset.pack;
      const packNum = parseInt(card.dataset.pack, 10) || 1;

      this.syncMovedFormControls();
      this.syncPackQuantityRules(packNum);
      this.setQuantityValue(packNum);

      this.cards.forEach((c) => {
        const on = c === card;
        c.classList.toggle('is-selected', on);
        c.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      if (focusCard) {
        try {
          card.focus({ preventScroll: true });
        } catch (err) {
          card.focus();
        }
      }
      this.updateUi();
    }

    getLineQuantity() {
      return Math.max(1, parseInt(this.quantityInput?.value, 10) || parseInt(this.selectedPack, 10) || 1);
    }

    getBundleCount(packNum, lineQty) {
      const pack = Math.max(1, packNum);
      return Math.max(1, Math.ceil(lineQty / pack));
    }

    updateUi() {
      const card = this.getSelectedCard();
      if (!card) return;

      const packNum = parseInt(card.dataset.pack, 10) || 1;
      const lineQty = this.getLineQuantity();
      const bundleCount = this.getBundleCount(packNum, lineQty);
      const packPrice = parseInt(card.dataset.price, 10) || 0;
      const savings = parseInt(card.dataset.savings, 10) || 0;
      const lineTotal = packPrice * bundleCount;
      const lineSavings = savings * bundleCount;

      if (this.shipLine) {
        const location = this.inventoryLocation;
        if (location) {
          this.shipLine.textContent = `${packNum} pack ships fresh from ${location}`;
          this.shipLine.hidden = false;
        } else {
          this.shipLine.hidden = true;
        }
      }

      if (this.savingsLine && this.savingsAmount) {
        if (lineSavings > 0) {
          this.savingsLine.hidden = false;
          this.savingsAmount.textContent = formatMoney(lineSavings);
        } else {
          this.savingsLine.hidden = true;
        }
      }

      if (this.summaryTotal) {
        this.summaryTotal.textContent = formatMoney(lineTotal);
      }

      if (this.submitLabel) {
        const unitLabel = 'PACK';
        const label = this.cartButtonLabel
          .replace(/\{\{\s*count\s*\}\}/g, String(lineQty))
          .replace(/\{\{\s*unit\s*\}\}/g, unitLabel)
          .replace(/\{\{\s*price\s*\}\}/g, formatMoney(lineTotal));
        this.submitLabel.textContent = label;
      }

      if (this.packProperty) {
        this.packProperty.value = String(packNum);
      }

      if (this.submitButton) {
        const available = this.dataset.available === 'true';
        this.submitButton.disabled = !available;
      }

      this.syncQuantityButtonState();
    }
  }

  if (!customElements.get('multi-pack-picker')) {
    customElements.define('multi-pack-picker', MultiPackPicker);
  }
})();
